import {
  ArenaActiveDuel,
  ArenaCapacityWaitPayload,
  ArenaDetails,
  ArenaEnteredPayload,
  ArenaMatchCardsPayload,
  ArenaMatchEvent,
  ArenaMatchEventType,
  ArenaMatchFinishedPayload,
  ArenaMatchFoundPayload,
  ArenaMatchPhase,
  ArenaMatchPlayer,
  ArenaMatchRewards,
  ArenaMatchSnapshot,
  ArenaOnlineUpdatedPayload,
  ArenaPlayerMatchState,
  ArenaPlayerStatus,
  ArenaQueuedPayload,
  ArenaSlotNumber,
  ArenaStatusPayload,
  ArenaSummary,
  ArenaTeamId,
} from "../types/arena"
import { BattleOutcome } from "../types/battle"
import { normalizeBattleCardView } from "./battleCards"

function unwrap(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as Record<string, unknown>
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return obj
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickNumberOrNull(value: unknown): number | null {
  if (value == null || value === "") return null
  const n = pickNumber(value, Number.NaN)
  return Number.isFinite(n) ? n : null
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickStringOrNull(value: unknown): string | null {
  if (value == null) return null
  const s = pickString(value).trim()
  return s || null
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === "true" || value === 1) return true
  if (value === "false" || value === 0) return false
  return fallback
}

export function normalizeArenaPlayerStatus(value: unknown): ArenaPlayerStatus {
  const raw = pickString(value).toUpperCase()
  if (
    raw === "ON_ARENA" ||
    raw === "IN_QUEUE" ||
    raw === "IN_MATCH" ||
    raw === "CAPACITY_WAIT"
  ) {
    return raw
  }
  return "IDLE"
}

export function normalizeArenaMatchPhase(value: unknown): ArenaMatchPhase {
  const raw = pickString(value).toUpperCase()
  if (
    raw === "QUEUE" ||
    raw === "SLOT_PICK" ||
    raw === "COMBAT" ||
    raw === "FINISHED"
  ) {
    return raw
  }
  if (raw === "SLOTS" || raw === "SLOT" || raw === "PICK") return "SLOT_PICK"
  if (raw === "FIGHT" || raw === "DUEL" || raw === "BATTLE") return "COMBAT"
  if (raw === "RESULT" || raw === "END" || raw === "DONE") return "FINISHED"
  return "LOBBY"
}

export function normalizeArenaTeamId(value: unknown): ArenaTeamId | null {
  const raw = pickString(value).toUpperCase()
  if (raw === "A" || raw === "TEAM_A" || raw === "1") return "A"
  if (raw === "B" || raw === "TEAM_B" || raw === "2") return "B"
  return null
}

export function normalizeArenaSlot(value: unknown): ArenaSlotNumber | null {
  const n = pickNumberOrNull(value)
  if (n === 1 || n === 2 || n === 3) return n
  return null
}

function normalizePlayerState(value: unknown): ArenaPlayerMatchState {
  const raw = pickString(value).toUpperCase()
  if (raw === "DEAD" || raw === "ELIMINATED" || raw === "OUT") {
    return raw === "ELIMINATED" ? "ELIMINATED" : "DEAD"
  }
  return "ALIVE"
}

export function normalizeArenaSummary(raw: unknown): ArenaSummary | null {
  const obj = unwrap(raw)
  const arenaId = pickString(obj.arenaId ?? obj.arena_id ?? obj.id)
  if (!arenaId) return null
  return {
    arenaId,
    name: pickString(obj.name ?? obj.title, "Арена"),
    cityId: pickNumberOrNull(obj.cityId ?? obj.city_id),
    cityName: pickStringOrNull(obj.cityName ?? obj.city_name ?? obj.city),
    onlineCount: pickNumber(obj.onlineCount ?? obj.online_count ?? obj.online),
    queueSize: pickNumber(obj.queueSize ?? obj.queue_size),
    activeMatches: pickNumber(
      obj.activeMatches ?? obj.active_matches ?? obj.matches
    ),
    capacityFull: pickBool(
      obj.capacityFull ?? obj.capacity_full ?? obj.isFull ?? obj.is_full
    ),
  }
}

export function normalizeArenaList(raw: unknown): ArenaSummary[] {
  const root = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  const list =
    (Array.isArray(raw) && raw) ||
    (Array.isArray(root.arenas) && root.arenas) ||
    (Array.isArray(root.items) && root.items) ||
    (Array.isArray(root.data) && root.data) ||
    (Array.isArray(unwrap(raw).arenas) && unwrap(raw).arenas) ||
    []

  return (list as unknown[])
    .map(normalizeArenaSummary)
    .filter((item): item is ArenaSummary => item != null)
}

export function normalizeArenaDetails(raw: unknown): ArenaDetails | null {
  const base = normalizeArenaSummary(raw)
  if (!base) return null
  const obj = unwrap(raw)
  return {
    ...base,
    description: pickStringOrNull(obj.description ?? obj.desc),
    maxConcurrentMatches: pickNumberOrNull(
      obj.maxConcurrentMatches ?? obj.max_concurrent_matches ?? obj.capacity
    ),
  }
}

export function normalizeArenaStatus(raw: unknown): ArenaStatusPayload {
  const obj = unwrap(raw)
  return {
    status: normalizeArenaPlayerStatus(obj.status ?? obj.state),
    arenaId: pickStringOrNull(obj.arenaId ?? obj.arena_id),
    arenaName: pickStringOrNull(obj.arenaName ?? obj.arena_name),
    matchId: pickStringOrNull(obj.matchId ?? obj.match_id),
    queueSize: pickNumberOrNull(obj.queueSize ?? obj.queue_size),
    needed: pickNumberOrNull(obj.needed ?? obj.neededPlayers ?? obj.needed_players),
    message: pickStringOrNull(obj.message),
  }
}

export function normalizeArenaEntered(raw: unknown): ArenaEnteredPayload | null {
  const obj = unwrap(raw)
  const arenaId = pickString(obj.arenaId ?? obj.arena_id)
  if (!arenaId) return null
  return {
    arenaId,
    arenaName: pickString(obj.arenaName ?? obj.arena_name ?? obj.name, "Арена"),
    onlineCount: pickNumber(obj.onlineCount ?? obj.online_count),
    queueSize: pickNumber(obj.queueSize ?? obj.queue_size),
    status: normalizeArenaPlayerStatus(obj.status ?? "ON_ARENA"),
  }
}

export function normalizeArenaOnlineUpdated(
  raw: unknown
): ArenaOnlineUpdatedPayload | null {
  const obj = unwrap(raw)
  const arenaId = pickString(obj.arenaId ?? obj.arena_id)
  if (!arenaId) return null
  return {
    arenaId,
    onlineCount: pickNumber(obj.onlineCount ?? obj.online_count),
  }
}

export function normalizeArenaQueued(raw: unknown): ArenaQueuedPayload {
  const obj = unwrap(raw)
  return {
    queueSize: pickNumber(obj.queueSize ?? obj.queue_size),
    needed: pickNumber(obj.needed ?? obj.neededPlayers ?? obj.needed_players, 6),
    message: pickStringOrNull(obj.message) ?? undefined,
  }
}

export function normalizeArenaCapacityWait(
  raw: unknown
): ArenaCapacityWaitPayload {
  const obj = unwrap(raw)
  return {
    message:
      pickStringOrNull(obj.message) ?? "Арены заняты, ожидание…",
    queueSize: pickNumberOrNull(obj.queueSize ?? obj.queue_size) ?? undefined,
    needed: pickNumberOrNull(obj.needed) ?? undefined,
  }
}

export function normalizeArenaMatchPlayer(raw: unknown): ArenaMatchPlayer | null {
  if (!raw || typeof raw !== "object") return null
  const obj = raw as Record<string, unknown>
  const userId = pickNumberOrNull(obj.userId ?? obj.user_id ?? obj.id)
  if (userId == null) return null
  const team = normalizeArenaTeamId(obj.team ?? obj.teamId ?? obj.team_id) ?? "A"
  return {
    userId,
    nickname: pickString(obj.nickname ?? obj.name, `Игрок ${userId}`),
    team,
    slot: normalizeArenaSlot(obj.slot ?? obj.slotNumber ?? obj.slot_number),
    state: normalizePlayerState(obj.state ?? obj.status),
    publicGameCode: pickStringOrNull(
      obj.publicGameCode ?? obj.public_game_code
    ),
  }
}

function normalizePlayers(raw: unknown): ArenaMatchPlayer[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(normalizeArenaMatchPlayer)
    .filter((p): p is ArenaMatchPlayer => p != null)
}

export function normalizeArenaMatchFound(
  raw: unknown
): ArenaMatchFoundPayload | null {
  const obj = unwrap(raw)
  const matchId = pickString(obj.matchId ?? obj.match_id)
  if (!matchId) return null
  const team = normalizeArenaTeamId(obj.team ?? obj.myTeam ?? obj.my_team) ?? "A"
  return {
    matchId,
    team,
    slotDeadlineAt: pickString(
      obj.slotDeadlineAt ?? obj.slot_deadline_at ?? obj.deadlineAt
    ),
    players: normalizePlayers(obj.players ?? obj.roster ?? obj.teams),
    neededSlots: pickNumberOrNull(obj.neededSlots ?? obj.needed_slots) ?? undefined,
  }
}

function normalizeRewards(raw: unknown): ArenaMatchRewards {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  return {
    ptsDelta: pickNumber(obj.ptsDelta ?? obj.pts_delta ?? obj.pts),
    coinsDelta: pickNumber(obj.coinsDelta ?? obj.coins_delta ?? obj.coins),
    dustDelta: pickNumber(obj.dustDelta ?? obj.dust_delta ?? obj.dust),
  }
}

function normalizeOutcome(value: unknown): BattleOutcome {
  const raw = pickString(value).toUpperCase()
  if (raw === "WIN" || raw === "VICTORY") return "WIN"
  if (raw === "LOSE" || raw === "LOSS" || raw === "DEFEAT") return "LOSE"
  return "DRAW"
}

export function normalizeArenaMatchFinished(
  raw: unknown
): ArenaMatchFinishedPayload | null {
  const obj = unwrap(raw)
  const matchId = pickString(obj.matchId ?? obj.match_id)
  if (!matchId) return null
  const rewardsRaw =
    obj.rewards ?? obj.deltas ?? obj.myRewards ?? obj.my_rewards ?? obj
  return {
    matchId,
    winningTeam: normalizeArenaTeamId(
      obj.winningTeam ?? obj.winning_team ?? obj.winnerTeam ?? obj.winner
    ),
    outcome: normalizeOutcome(obj.outcome ?? obj.result ?? obj.myOutcome),
    rewards: normalizeRewards(rewardsRaw),
    message: pickStringOrNull(obj.message) ?? undefined,
  }
}

export function normalizeArenaActiveDuel(raw: unknown): ArenaActiveDuel | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const duelId = pickString(obj.duelId ?? obj.duel_id ?? obj.id)
  const teamAUserId = pickNumberOrNull(
    obj.teamAUserId ?? obj.team_a_user_id ?? obj.playerAUserId ?? obj.aUserId
  )
  const teamBUserId = pickNumberOrNull(
    obj.teamBUserId ?? obj.team_b_user_id ?? obj.playerBUserId ?? obj.bUserId
  )
  if (!duelId || teamAUserId == null || teamBUserId == null) return null
  return {
    duelId,
    teamAUserId,
    teamBUserId,
    teamANickname: pickString(
      obj.teamANickname ?? obj.team_a_nickname ?? obj.playerANickname,
      `Игрок ${teamAUserId}`
    ),
    teamBNickname: pickString(
      obj.teamBNickname ?? obj.team_b_nickname ?? obj.playerBNickname,
      `Игрок ${teamBUserId}`
    ),
    cardDeadlineAt: pickStringOrNull(
      obj.cardDeadlineAt ?? obj.card_deadline_at ?? obj.deadlineAt
    ),
    yourTurn: pickBool(obj.yourTurn ?? obj.your_turn ?? obj.isYourTurn),
    selectedCardId: pickStringOrNull(
      obj.selectedCardId ?? obj.selected_card_id
    ),
  }
}

const EVENT_TYPES = new Set<ArenaMatchEventType>([
  "MATCH_FOUND",
  "SLOT_CLAIMED",
  "SLOTS_AUTO_ASSIGNED",
  "DUEL_STARTED",
  "CARD_SELECTED",
  "DUEL_RESOLVED",
  "DUEL_TIMEOUT",
  "PLAYER_FORFEIT",
  "PLAYER_DISCONNECT_ELIMINATED",
  "MATCH_FINISHED",
])

export function normalizeArenaMatchEventType(
  value: unknown
): ArenaMatchEventType | null {
  const raw = pickString(value).toUpperCase() as ArenaMatchEventType
  return EVENT_TYPES.has(raw) ? raw : null
}

export function normalizeArenaMatchEvent(raw: unknown): ArenaMatchEvent | null {
  const obj = unwrap(raw)
  const matchId = pickString(obj.matchId ?? obj.match_id)
  const eventType = normalizeArenaMatchEventType(
    obj.eventType ?? obj.event_type ?? obj.type
  )
  if (!matchId || !eventType) return null
  const payloadRaw = obj.payload
  const payload =
    payloadRaw && typeof payloadRaw === "object" && !Array.isArray(payloadRaw)
      ? (payloadRaw as Record<string, unknown>)
      : {}
  return {
    matchId,
    seq: pickNumber(obj.seq ?? obj.sequence ?? obj.eventSeq),
    phase: normalizeArenaMatchPhase(obj.phase),
    eventType,
    payload,
    at: pickString(obj.at ?? obj.createdAt ?? obj.created_at, new Date().toISOString()),
  }
}

function normalizeBurnedIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((id) => pickString(id))
    .filter(Boolean)
}

export function normalizeArenaMatchCards(
  raw: unknown
): ArenaMatchCardsPayload | null {
  const obj = unwrap(raw)
  const matchId = pickString(obj.matchId ?? obj.match_id)
  if (!matchId) return null
  const cardsRaw = obj.yourCards ?? obj.your_cards ?? obj.cards ?? []
  const yourCards = Array.isArray(cardsRaw)
    ? cardsRaw
        .map(normalizeBattleCardView)
        .filter((c): c is NonNullable<typeof c> => c != null)
    : []
  return {
    matchId,
    yourCards,
    burnedCardIds: normalizeBurnedIds(
      obj.burnedCardIds ?? obj.burned_card_ids ?? obj.burned
    ),
  }
}

export function normalizeArenaMatchSnapshot(
  raw: unknown
): ArenaMatchSnapshot | null {
  const obj = unwrap(raw)
  const matchId = pickString(obj.matchId ?? obj.match_id)
  if (!matchId) return null

  const cards = normalizeArenaMatchCards(obj)
  const logRaw = obj.log ?? obj.events ?? obj.history
  const log = Array.isArray(logRaw)
    ? logRaw
        .map(normalizeArenaMatchEvent)
        .filter((e): e is ArenaMatchEvent => e != null)
    : []

  const finishedRaw =
    obj.finished ?? obj.result ?? obj.matchFinished ?? obj.match_finished
  const finished = finishedRaw
    ? normalizeArenaMatchFinished({
        ...(typeof finishedRaw === "object" ? finishedRaw : {}),
        matchId,
      })
    : null

  return {
    matchId,
    phase: normalizeArenaMatchPhase(obj.phase ?? obj.status),
    seq: pickNumber(obj.seq ?? obj.lastSeq ?? obj.last_seq),
    team: normalizeArenaTeamId(obj.team ?? obj.myTeam ?? obj.my_team),
    myUserId: pickNumberOrNull(obj.myUserId ?? obj.my_user_id ?? obj.userId),
    slotDeadlineAt: pickStringOrNull(
      obj.slotDeadlineAt ?? obj.slot_deadline_at
    ),
    players: normalizePlayers(obj.players ?? obj.roster),
    activeDuel: normalizeArenaActiveDuel(
      obj.activeDuel ?? obj.active_duel ?? obj.duel
    ),
    log,
    finished,
    yourCards: cards?.yourCards ?? [],
    burnedCardIds: cards?.burnedCardIds ?? [],
  }
}

export function formatArenaEventLogLine(event: ArenaMatchEvent): string {
  const p = event.payload
  switch (event.eventType) {
    case "MATCH_FOUND":
      return "Матч найден. Выберите слот."
    case "SLOT_CLAIMED": {
      const nick = pickString(p.nickname ?? p.playerNickname, "Игрок")
      const slot = pickNumberOrNull(p.slot)
      return slot != null
        ? `${nick} занял слот ${slot}`
        : `${nick} занял слот`
    }
    case "SLOTS_AUTO_ASSIGNED":
      return "Слоты назначены автоматически"
    case "DUEL_STARTED": {
      const a = pickString(p.teamANickname ?? p.aNickname, "A")
      const b = pickString(p.teamBNickname ?? p.bNickname, "B")
      return `Дуэль: ${a} vs ${b}`
    }
    case "CARD_SELECTED": {
      const nick = pickString(p.nickname, "Игрок")
      return `${nick} выбрал карту`
    }
    case "DUEL_RESOLVED": {
      const winner = pickString(p.winnerNickname ?? p.winner, "")
      const draw = pickBool(p.draw ?? p.isDraw) || !winner
      if (draw) return "Ничья в дуэли — оба выбывают"
      return `Победа в дуэли: ${winner}`
    }
    case "DUEL_TIMEOUT": {
      const nick = pickString(p.nickname ?? p.playerNickname, "Игрок")
      return `Таймаут хода: ${nick} выбывает`
    }
    case "PLAYER_FORFEIT": {
      const nick = pickString(p.nickname, "Игрок")
      return `${nick} сдался`
    }
    case "PLAYER_DISCONNECT_ELIMINATED": {
      const nick = pickString(p.nickname, "Игрок")
      return `${nick} отключился и выбыл`
    }
    case "MATCH_FINISHED":
      return "Матч завершён"
    default:
      return event.eventType
  }
}

export function isArenaUiLocked(status: ArenaPlayerStatus): boolean {
  return (
    status === "ON_ARENA" ||
    status === "IN_QUEUE" ||
    status === "IN_MATCH" ||
    status === "CAPACITY_WAIT"
  )
}
